// src/pages/network/DiscoverPage.js
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, Search, Filter, Building, Globe, 
  UserPlus, Users, X, AlertCircle, Compass
} from 'lucide-react';
import { db } from '../../firebase/config';
import { 
  collection, query, where, orderBy, limit, 
  getDocs, doc, setDoc, serverTimestamp,
  deleteDoc, getDoc
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

const DiscoverPage = () => {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [connectionStatus, setConnectionStatus] = useState({});
  const [industries, setIndustries] = useState([]);
  const [error, setError] = useState(null);
  
  // Update search term from URL params
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchTerm(searchFromUrl);
    }
  }, [searchParams]);

  // Fetch businesses for discovery
  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Create a query to get businesses
        const businessesQuery = query(
          collection(db, 'businesses'),
          orderBy('name'),
          limit(30)
        );

        const businessesSnap = await getDocs(businessesQuery);
        const businessesList = [];

        businessesSnap.forEach(doc => {
          const data = doc.data();
          // Exclude current user's own business in JS
          if (currentUser && data.createdBy === currentUser.uid) return;
          businessesList.push({
            id: doc.id,
            ...data
          });
        });
        
        setBusinesses(businessesList);
        
        // Extract unique industries for filter
        const uniqueIndustries = [...new Set(
          businessesList
            .map(business => business.industry)
            .filter(industry => industry && industry.trim() !== '')
        )];
        setIndustries(uniqueIndustries);
        
        // If user is logged in, check connection status with these businesses
        if (currentUser) {
          // Get all connections where user is a participant
          const connectionsQuery = query(
            collection(db, 'connections'),
            where('participants', 'array-contains', currentUser.uid)
          );
          
          const connectionsSnap = await getDocs(connectionsQuery);
          const statuses = {};
          
          connectionsSnap.forEach(doc => {
            const connection = doc.data();
            const otherBusinessId = connection.participants.find(id => id !== currentUser.uid);
            
            if (otherBusinessId) {
              statuses[otherBusinessId] = {
                status: connection.status,
                connectionId: doc.id,
                requesterId: connection.requesterId,
              };
            }
          });
          
          setConnectionStatus(statuses);
        }
      } catch (err) {
        console.error('Error fetching businesses:', err);
        setError('Failed to load businesses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBusinesses();
  }, [currentUser]);
  
  // Send connection request
  const handleSendRequest = async (businessId) => {
    if (!currentUser) {
      // Redirect to login if not authenticated
      window.location.href = '/login?redirect=/network/discover';
      return;
    }
    
    try {
      // Create a new connection document
      const connectionId = uuidv4();
      
      await setDoc(doc(db, 'connections', connectionId), {
        requesterId: currentUser.uid,
        receiverId: businessId,
        status: 'pending',
        createdAt: serverTimestamp(),
        participants: [currentUser.uid, businessId]
      });
      
      // Update local state
      setConnectionStatus(prev => ({
        ...prev,
        [businessId]: {
          status: 'pending',
          connectionId,
          requesterId: currentUser.uid
        }
      }));
    } catch (err) {
      console.error('Error sending connection request:', err);
      alert('Failed to send connection request. Please try again.');
    }
  };
  
  // Cancel connection request
  const handleCancelRequest = async (businessId) => {
    try {
      const connectionId = connectionStatus[businessId]?.connectionId;
      
      if (connectionId) {
        await deleteDoc(doc(db, 'connections', connectionId));
        
        // Update local state
        setConnectionStatus(prev => {
          const updated = { ...prev };
          delete updated[businessId];
          return updated;
        });
      }
    } catch (err) {
      console.error('Error canceling connection request:', err);
      alert('Failed to cancel connection request. Please try again.');
    }
  };
  
  // Get filtered businesses
  const getFilteredBusinesses = () => {
    return businesses.filter(business => {
      // Apply search filter
      const matchesSearch = !searchTerm || 
        business.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Apply industry filter
      const matchesIndustry = industryFilter === 'all' || 
        business.industry === industryFilter;
      
      return matchesSearch && matchesIndustry;
    });
  };
  
  // Get connection button text and status
  const getConnectionStatus = (businessId) => {
    const status = connectionStatus[businessId];
    
    if (!status) {
      return { text: 'Connect', status: 'none' };
    }
    
    if (status.status === 'pending') {
      if (status.requesterId === currentUser?.uid) {
        return { text: 'Cancel Request', status: 'sent' };
      } else {
        return { text: 'Respond', status: 'received' };
      }
    }
    
    if (status.status === 'accepted') {
      return { text: 'Connected', status: 'connected' };
    }
    
    return { text: 'Connect', status: 'none' };
  };
  
  const filteredBusinesses = getFilteredBusinesses();
  
  return (
    <div className="container mx-auto max-w-6xl px-4">
      <div className="mb-6">
        <Link 
          to="/network"
          className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Network
        </Link>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Discover Businesses</h1>
          <p className="text-gray-600">Find and connect with other businesses in your industry</p>
        </div>
        
        <div className="p-6">
          {/* Search and filter section */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                placeholder="Search by name, industry, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <select
                className="pl-10 pr-4 py-2.5 rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20 appearance-none"
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
              >
                <option value="all">All Industries</option>
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Results section */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 rounded-lg py-6 px-6 text-center">
              <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-[#2C0DB5]"
              >
                Try Again
              </button>
            </div>
          ) : filteredBusinesses.length === 0 ? (
            <div className="text-center py-12">
              <Compass size={48} className="mx-auto text-gray-300 mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No businesses found</h3>
              <p className="text-gray-500 max-w-md mx-auto mb-4">
                {searchTerm || industryFilter !== 'all' ? 
                  "We couldn't find any businesses matching your search criteria." :
                  "There are no businesses in our network yet."}
              </p>
              {(searchTerm || industryFilter !== 'all') && (
                <button
                  onClick={() => { setSearchTerm(''); setIndustryFilter('all'); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBusinesses.map(business => {
                const connection = getConnectionStatus(business.id);
                
                return (
                  <div 
                    key={business.id}
                    className="border border-gray-200 rounded-xl overflow-hidden flex flex-col"
                  >
                    <div className="p-4 flex">
                      <div className="w-16 h-16 rounded-lg bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                        {business.businessLogoUrl ? (
                          <img 
                            src={business.businessLogoUrl} 
                            alt={business.name} 
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                            <Building size={24} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/network/profile/${business.id}`}
                          className="text-gray-900 font-medium hover:text-[#190192]"
                        >
                          {business.name}
                        </Link>
                        <p className="text-gray-500 text-sm">{business.industry || 'Business'}</p>
                        {business.employees && (
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <Users size={12} className="mr-1" />
                            <span>{business.employees} employees</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {business.description && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {business.description}
                        </p>
                      </div>
                    )}
                    
                    <div className="mt-auto border-t border-gray-100 p-3 flex justify-between items-center">
                      <div className="flex items-center">
                        {business.website && (
                          <a
                            href={
                              business.website.startsWith('http')
                                ? business.website
                                : `https://${business.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-500 hover:text-[#190192]"
                            title="Visit Website"
                          >
                            <Globe size={16} />
                          </a>
                        )}
                      </div>
                    </div>

                      {currentUser && (
                        <div>
                          {connection.status === 'none' && (
                            <button
                              onClick={() => handleSendRequest(business.id)}
                              className="px-3 py-1.5 text-[#190192] text-sm font-medium rounded-lg hover:bg-purple-50 flex items-center"
                            >
                              <UserPlus size={16} className="mr-1" />
                              Connect
                            </button>
                          )}
                          
                          {connection.status === 'sent' && (
                            <button
                              onClick={() => handleCancelRequest(business.id)}
                              className="px-3 py-1.5 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 flex items-center"
                            >
                              <X size={16} className="mr-1" />
                              Cancel Request
                            </button>
                          )}
                          
                          {connection.status === 'received' && (
                            <Link
                              to="/network/connections"
                              className="px-3 py-1.5 text-[#190192] text-sm font-medium rounded-lg hover:bg-purple-50 flex items-center"
                            >
                              Respond to Request
                            </Link>
                          )}
                          
                          {connection.status === 'connected' && (
                            <span className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-lg flex items-center">
                              <Users size={16} className="mr-1" />
                              Connected
                            </span>
                          )}
                        </div>
                      )}
                      
                      {!currentUser && (
                        <Link
                          to={`/login?redirect=/network/discover`}
                          className="px-3 py-1.5 text-[#190192] text-sm font-medium rounded-lg hover:bg-purple-50 flex items-center"
                        >
                          <UserPlus size={16} className="mr-1" />
                          Sign in to Connect
                        </Link>
                      )}
                    </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscoverPage;